import {
    getSolarizationTierKey,
    getPathogenSolarizationTierKey,
    PATHOGEN_LETHAL_THERMAL_INTEGRAL
} from '../utils/fieldUtils';

export const FIELD_CHART_MODAL_HEIGHT_PX = 380;

export const SOLAR_PATHOGEN_CHART_LINE_COLORS = {
    FOM: '#C0392B',
    PC: '#8E44AD',
    RS: '#2980B9',
    SS: '#16A085'
};

export const getChartModalTimelineMarkerColor = (status) => {
    if (status == null || status === '') return '#9e9e9e';
    if (status === 'start_model') return '#9e9e9e';
    if (status.includes('alert')) return '#ED5F55';
    if (status.includes('warning')) return '#F6894E';
    const redSolar = new Set([
        'null',
        'low',
        'moderate',
        'solar_pathogen_low',
        'solar_pathogen_moderate'
    ]);
    if (redSolar.has(status)) return '#ED5F55';
    if (status === 'good' || status === 'solar_pathogen_good') return '#F6894E';
    const greenSolar = new Set(['excellent', 'super', 'solar_pathogen_excellent', 'solar_pathogen_super']);
    if (greenSolar.has(status)) return '#7ECA22';
    return 'var(--primary-color)';
};

const chartColorFromSolarTierKey = (tierKey) => {
    if (tierKey === 'null' || tierKey === 'low' || tierKey === 'moderate') return '#ED5F55';
    if (tierKey === 'good') return '#F6894E';
    if (tierKey === 'excellent' || tierKey === 'super') return '#7ECA22';
    return '#7ECA22';
};

export const nematodeSolarLineColorAtDegrees = (degrees) =>
    chartColorFromSolarTierKey(getSolarizationTierKey(degrees));

export const pathogenSolarLineColorAtDegrees = (degrees, pathogenCode) =>
    chartColorFromSolarTierKey(getPathogenSolarizationTierKey(degrees, pathogenCode));

export const solarLogDate = (entry) => {
    if (!entry || typeof entry !== 'object') return null;
    const v =
        entry.date ??
        entry.timestamp ??
        entry.logged_at ??
        entry.loggedAt ??
        entry.created_at ??
        entry.createdAt ??
        entry.time;
    if (v == null || v === '') return null;
    return v;
};

export const buildNematodeTimelineEventsFromPrediction = (modelData, modelDataJ2, fieldData, modelStartDateForEvent) => {
    const modelStartEvent = [];
    if (
        fieldData &&
        fieldData.sensors.length > 0 &&
        (fieldData.sensors[0].license_info.nematode_ovicide || fieldData.sensors[0].license_info.nematode_larvicide)
    ) {
        modelStartEvent.push({
            status: 'start_model',
            date: modelStartDateForEvent
        });
    }

    const uniqueGenerations = [...new Set((modelData || []).map((entry) => entry.generation))];

    const eggWarnings = [];
    const eggAlerts = [];

    if (fieldData && fieldData.sensors.length > 0 && fieldData.sensors[0].license_info.nematode_ovicide) {
        uniqueGenerations.forEach((gen) => {
            const genEntries = (modelData || []).filter((entry) => entry.generation === gen);

            const genEggWarnings = genEntries
                .filter((entry) => entry.ratio_eggs * 100 >= 80 && entry.ratio_eggs * 100 < 100)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            if (genEggWarnings.length > 0) {
                eggWarnings.push({
                    status: 'nematode_head_warning_eggs',
                    date: genEggWarnings[0].date,
                    generation: gen
                });
            }

            const genEggAlerts = genEntries
                .filter((entry) => entry.ratio_eggs * 100 >= 100)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            if (genEggAlerts.length > 0) {
                eggAlerts.push({
                    status: 'nematode_head_alert_eggs',
                    date: genEggAlerts[0].date,
                    generation: gen
                });
            }
        });
    }

    const larvaeWarnings = [];
    const larvaeAlerts = [];

    if (fieldData && fieldData.sensors.length > 0 && fieldData.sensors[0].license_info.nematode_larvicide) {
        const uniqueGenerationsJ2 = [...new Set((modelDataJ2 || []).map((entry) => entry.generation))];
        const firstGenInModelJ2 = uniqueGenerationsJ2.length
            ? Math.min(...uniqueGenerationsJ2.map((g) => Number(g)))
            : null;

        uniqueGenerationsJ2.forEach((gen) => {
            const genEntriesJ2 = (modelDataJ2 || []).filter((entry) => entry.generation === gen);

            const genLarvaeWarnings = genEntriesJ2
                .filter((entry) => entry.ratio_larvicide >= 80 && entry.ratio_larvicide < 100)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            if (genLarvaeWarnings.length > 0 && firstGenInModelJ2 != null && Number(gen) !== firstGenInModelJ2) {
                larvaeWarnings.push({
                    status: 'nematode_head_warning_larvae',
                    date: genLarvaeWarnings[0].date,
                    generation: gen
                });
            }

            const genLarvaeAlerts = genEntriesJ2
                .filter((entry) => entry.ratio_larvicide >= 100)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            if (genLarvaeAlerts.length > 0) {
                larvaeAlerts.push({
                    status: 'nematode_head_alert_larvae',
                    date: genLarvaeAlerts[0].date,
                    generation: gen
                });
            }
        });
    }

    return sortTimelineEventsChronologically([
        ...eggWarnings,
        ...eggAlerts,
        ...larvaeWarnings,
        ...larvaeAlerts,
        ...modelStartEvent
    ]);
};

export const filterSolarTimelineLogEntries = (entries, rangeStartDate, rangeEndInclusiveDate, getRawDate) => {
    const t0 = rangeStartDate.getTime();
    const t1 = rangeEndInclusiveDate.getTime();
    return (entries || []).filter((e) => {
        const raw = getRawDate(e);
        if (raw == null || raw === '') return false;
        const t = new Date(String(raw).replace(/Z$/i, '')).getTime();
        return !Number.isNaN(t) && t >= t0 && t <= t1;
    });
};

export const solarLogDegrees = (entry) => {
    if (!entry || typeof entry !== 'object') return null;
    const v =
        entry.degrees ??
        entry.degree ??
        entry.cum_thermal_integral ??
        entry.cumThermalIntegral ??
        entry.accumulated_degrees ??
        entry.accumulatedDegrees ??
        entry.total_degrees ??
        entry.totalDegrees ??
        entry.degree_days ??
        entry.degreeDays ??
        entry.cumulative_degrees ??
        entry.cumulativeDegrees ??
        entry.thermal_integral ??
        entry.thermalIntegral ??
        entry.value;
    if (v === undefined || v === null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

export const parseChartLabelToTime = (label, interval) => {
    const s = String(label).trim();
    const spaceIdx = s.indexOf(' ');
    const datePart = spaceIdx >= 0 ? s.slice(0, spaceIdx) : s;
    const timePart = spaceIdx >= 0 ? s.slice(spaceIdx + 1).trim() : '';
    const parts = datePart.split('-');
    if (parts.length !== 3) return NaN;
    const [da, mo, yr] = parts.map((x) => parseInt(x, 10));
    if (![da, mo, yr].every((n) => Number.isFinite(n))) return NaN;
    let hour = 0;
    if (interval === 'H' && timePart) {
        const hp = timePart.split(':');
        hour = parseInt(hp[0], 10);
        if (!Number.isFinite(hour)) hour = 0;
    }
    return new Date(yr, mo - 1, da, hour, 0, 0, 0).getTime();
};

export const sortChartLabelsChronologically = (labelList, interval) =>
    [...labelList].sort((a, b) => {
        const ta = parseChartLabelToTime(a, interval);
        const tb = parseChartLabelToTime(b, interval);
        const na = Number.isNaN(ta);
        const nb = Number.isNaN(tb);
        if (na && nb) return String(a).localeCompare(String(b));
        if (na) return 1;
        if (nb) return -1;
        return ta - tb;
    });

/** Orden por día calendario (local); mismo día: inicio del modelo antes que el resto; luego por hora real. */
const startOfLocalCalendarDay = (dateStr) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return Number.NaN;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const compareTimelineEventsByDate = (a, b) => {
    const da = startOfLocalCalendarDay(a.date);
    const db = startOfLocalCalendarDay(b.date);
    if (Number.isNaN(da) && Number.isNaN(db)) return String(a.date ?? '').localeCompare(String(b.date ?? ''));
    if (Number.isNaN(da)) return 1;
    if (Number.isNaN(db)) return -1;
    if (da !== db) return da - db;
    const order = (e) => (e.status === 'start_model' ? 0 : 1);
    const oa = order(a);
    const ob = order(b);
    if (oa !== ob) return oa - ob;
    return new Date(a.date).getTime() - new Date(b.date).getTime();
};

export const sortTimelineEventsChronologically = (events) => [...(events || [])].sort(compareTimelineEventsByDate);

const PATHOGEN_SOLAR_TIMELINE_FRACS = [
    { frac: 1 / 5, statusKey: 'solar_pathogen_low' },
    { frac: 2 / 5, statusKey: 'solar_pathogen_moderate' },
    { frac: 3 / 5, statusKey: 'solar_pathogen_good' },
    { frac: 4 / 5, statusKey: 'solar_pathogen_excellent' },
    { frac: 1, statusKey: 'solar_pathogen_super' }
];

export const buildPathogenSolarTimelineEvents = (pathogenCode, rows, modelStartDate) => {
    const start = modelStartDate ? [{ status: 'start_model', date: modelStartDate }] : [];
    const L = PATHOGEN_LETHAL_THERMAL_INTEGRAL[pathogenCode];
    if (!L || L <= 0 || !Array.isArray(rows) || rows.length === 0) {
        return sortTimelineEventsChronologically(start);
    }
    const sorted = [...rows].sort((a, b) => {
        const ta = new Date(solarLogDate(a) || 0).getTime();
        const tb = new Date(solarLogDate(b) || 0).getTime();
        return ta - tb;
    });
    const tierByLogTime = new Map();
    for (const { frac, statusKey } of PATHOGEN_SOLAR_TIMELINE_FRACS) {
        const threshold = frac * L;
        const hit = sorted.find((e) => (solarLogDegrees(e) ?? 0) >= threshold);
        const d = hit ? solarLogDate(hit) : null;
        if (!d) continue;
        const t = new Date(d).getTime();
        if (Number.isNaN(t)) continue;
        tierByLogTime.set(t, { status: statusKey, date: d });
    }
    const tierHits = [...tierByLogTime.values()].sort((a, b) => new Date(a.date) - new Date(b.date));
    return sortTimelineEventsChronologically([...start, ...tierHits]);
};
